import CircleLink from '@/circle/components/CircleLink'
import MemberLink from '@/member/components/MemberLink'
import RoleEditLink from '@/role/components/RoleEditLink'
import { useOrgData } from '@/org/contexts/OrgContext'
import { LogFragment } from '@gql'
import { LogType } from '@rolebase/shared/model/log'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { logChangeSummary } from '../logChangeSummary'
import LogFieldsList from './LogFieldsList'

interface Props {
  log: LogFragment
}

export default function LogText({ log }: Props) {
  const { t } = useTranslation()
  const orgData = useOrgData()
  const memberId = log.cancelMemberId || log.memberId
  const memberName = log.cancelMemberName || log.memberName
  const type = log.display.type
  const i18nPrefix = 'LogText'

  const author = <MemberLink id={memberId} name={memberName} />

  switch (type) {
    case LogType.CircleCreate:
    case LogType.CircleMove:
    case LogType.CircleCopy:
      return (
        <Trans
          i18nKey={`${i18nPrefix}.${type}`}
          components={{
            author,
            circle: <CircleLink id={log.display.id} name={log.display.name} />,
            parentCircle:
              log.display.parentId && log.display.parentName ? (
                <CircleLink
                  id={log.display.parentId}
                  name={log.display.parentName}
                />
              ) : (
                <>{t(`${i18nPrefix}.circleRoot`)}</>
              ),
          }}
        />
      )

    case LogType.CircleArchive:
      return (
        <Trans
          i18nKey={`${i18nPrefix}.${type}`}
          components={{
            author,
            circle: <CircleLink id={log.display.id} name={log.display.name} />,
          }}
        />
      )

    case LogType.CircleMemberAdd:
    case LogType.CircleMemberRemove:
      return (
        <Trans
          i18nKey={`${i18nPrefix}.${type}`}
          components={{
            author,
            member: (
              <MemberLink
                id={log.display.memberId}
                name={log.display.memberName}
              />
            ),
            circle: <CircleLink id={log.display.id} name={log.display.name} />,
          }}
        />
      )

    case LogType.CircleLinkAdd:
    case LogType.CircleLinkRemove:
      return (
        <Trans
          i18nKey={`${i18nPrefix}.${type}`}
          components={{
            author,
            circle: <CircleLink id={log.display.id} name={log.display.name} />,
            invitedCircle: (
              <CircleLink
                id={log.display.circleId}
                name={log.display.circleName}
              />
            ),
          }}
        />
      )

    case LogType.RoleUpdate: {
      // What changed, said in the sentence (#114)
      const summary = logChangeSummary(log, (id) => orgData?.getRole(id)?.name)
      const role = <RoleEditLink id={log.display.id} name={log.display.name} />
      if (summary.kind === 'fields') {
        return (
          <Trans
            i18nKey={`${i18nPrefix}.RoleUpdateFields`}
            components={{
              author,
              role,
              fields: <LogFieldsList fields={summary.fields} />,
            }}
          />
        )
      }
      if (summary.kind === 'format') {
        return (
          <Trans
            i18nKey={`${i18nPrefix}.RoleUpdateFormat`}
            components={{ author, role }}
          />
        )
      }
      if (summary.kind === 'several') {
        return (
          <Trans
            i18nKey={`${i18nPrefix}.RoleUpdateSeveral`}
            values={{ count: summary.names.length }}
            components={{ author, roles: <>{summary.names.join(', ')}</> }}
          />
        )
      }
      return (
        <Trans
          i18nKey={`${i18nPrefix}.${type}`}
          components={{ author, role }}
        />
      )
    }

    case LogType.RoleCreate:
    case LogType.RoleArchive:
      return (
        <Trans
          i18nKey={`${i18nPrefix}.${type}`}
          components={{
            author,
            role: <RoleEditLink id={log.display.id} name={log.display.name} />,
          }}
        />
      )

    default:
      // Type hérité ou inconnu : jamais de nom technique à l'écran (#158)
      console.warn(`Log type ${type} is not supported`)
      return null
  }
}
